/**
 * Prepaid run time — the first deposit chosen as a duration, not a dollar sum.
 *
 * A beta tenant approved the old default of $0.50 at $0.90/hour, about 33
 * minutes, and only found out when the lease closed: "deposit" read as a
 * security deposit, and nothing on the review screen made them look at it.
 * The review page now asks how long the instance should run, and these are
 * the rules it does that with.
 *
 * The amount sent on chain is still a typed dollar string, checked by
 * `checkDeposit` and converted exactly as before. A choice only decides which
 * string is typed, and everything said about run time is read back from the
 * final amount (`runTimeReadout`), never from the label of the choice: at the
 * chain minimum a "1 hour" choice can buy a day, and the screen must say so.
 */

import { MIN_FIRST_DEPOSIT_MICRO, runwayMinutes, topUpForHours } from './deposit'
import { toMicroAmount } from './fees'
import type { PriceCoin } from './pricing'

export type RunTimeKey = 'hour' | 'day' | 'week' | 'month'

export const RUN_TIME_CHOICES: ReadonlyArray<{ key: RunTimeKey; hours: number; label: string }> = [
  { key: 'hour', hours: 1, label: '1 hour' },
  { key: 'day', hours: 24, label: '1 day' },
  { key: 'week', hours: 24 * 7, label: '1 week' },
  { key: 'month', hours: 24 * 30, label: '30 days' },
]

/**
 * When a running instance reads "running low" (decision D1): 24 hours left,
 * or 1 hour left when the whole deposit bought less than a day. Without the
 * second rule a deposit of a few hours would read "low" from its first block.
 * `depositMinutes` is what everything deposited buys at the rate; null when
 * unknown, which keeps the 24-hour rule.
 */
export function lowThresholdMinutes(depositMinutes: number | null): number {
  return depositMinutes !== null && depositMinutes < 24 * 60 ? 60 : 24 * 60
}

export function isRunningLow(runwayMinutes: number | null, depositMinutes: number | null): boolean {
  return runwayMinutes !== null && runwayMinutes < lowThresholdMinutes(depositMinutes)
}

/** Below this many minutes the tenant has to confirm the short run time. */
export const SHORT_RUN_MINUTES = 60

/** The bar's log scale runs from one hour to thirty days. */
const BAR_MIN_MINUTES = 60
const BAR_MAX_MINUTES = 30 * 24 * 60

function microToInput(micro: bigint): string {
  const cents = (micro + 9_999n) / 10_000n
  return `${(cents / 100n).toString()}.${(cents % 100n).toString().padStart(2, '0')}`
}

/**
 * The dollar input string a choice types: `hours` at `rate`, rounded UP to the
 * cent so the duration is never short, and never below the chain's minimum
 * first deposit (the chain refuses a smaller one). Null when the rate is
 * unreadable.
 */
export function amountForRunTime(rate: PriceCoin | null | undefined, hours: number, minimum: bigint = MIN_FIRST_DEPOSIT_MICRO): string | null {
  const dollars = topUpForHours(rate, hours)
  if (dollars === null) return null
  const micro = toMicroAmount(dollars)
  if (micro === null) return null
  return BigInt(micro) < minimum ? microToInput(minimum) : dollars
}

/**
 * The choice preselected on arrival (decision D2): one day, else the longest
 * shorter choice the wallet covers, else none. Never a choice under an hour:
 * every choice is at least an hour, and "none" makes the tenant pick.
 *
 * `available` is what the wallet can put into the deposit in `uddt`: its
 * whole balance, since the transaction carries no fee (the network's gas
 * price is zero, ADR-003). Null means the balance is unknown; one day is
 * preselected, and the caller's `checkDeposit` with the same `available`
 * refuses an amount the wallet cannot cover.
 */
export function defaultRunTime(rate: PriceCoin | null | undefined, available: bigint | null): RunTimeKey | null {
  const candidates: RunTimeKey[] = ['day', 'hour']
  for (const key of candidates) {
    const hours = RUN_TIME_CHOICES.find((choice) => choice.key === key)!.hours
    const amount = amountForRunTime(rate, hours)
    if (amount === null) return null
    if (available === null || BigInt(toMicroAmount(amount)!) <= available) return key
  }
  return null
}

/** "33 min", "6 hours", "1 day", "30 days" — floored, so never longer than bought. */
export function describeRunTime(minutes: number): string {
  if (minutes < 1) return 'under a minute'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'}`
}

export interface RunTimeReadout {
  minutes: number
  /** "1 day" — what the button and "Runs for about" say. */
  duration: string
  /** When the deposit is expected to run out, from `now`. */
  stopsAt: Date
  short: boolean
  /** 0..1 on a log scale from one hour to thirty days. */
  bar: number
}

/** What `micro` buys at `rate`, read from the final amount. Null when either is unreadable. */
export function runTimeReadout(micro: string | bigint, rate: PriceCoin | null | undefined, now: number): RunTimeReadout | null {
  const minutes = runwayMinutes(micro, rate)
  if (minutes === null) return null
  const span = Math.log(BAR_MAX_MINUTES / BAR_MIN_MINUTES)
  const bar = minutes <= BAR_MIN_MINUTES ? 0 : Math.min(1, Math.log(minutes / BAR_MIN_MINUTES) / span)
  return {
    minutes,
    duration: describeRunTime(minutes),
    stopsAt: new Date(now + minutes * 60_000),
    short: minutes < SHORT_RUN_MINUTES,
    bar,
  }
}

/** "Tue, Oct 7, 2:41 PM" in the viewer's locale and zone. */
export function formatStopTime(at: Date): string {
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(at)
}

/** "5:07" for a hold countdown; zero and below read "0:00". */
export function formatHold(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds))
  return `${Math.floor(whole / 60)}:${(whole % 60).toString().padStart(2, '0')}`
}
